import { useEffect, useState, useCallback } from "react";
import api, { mediaUrl, formatError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  PartyPopper, Wand2, Loader2, Tv, Check, CalendarDays, Send, Repeat, Maximize,
  EyeOff, Search, Radio, CalendarClock, Trash2,
} from "lucide-react";

// Shared push controls: choose display mode + target TVs, then push now or schedule.
function PushControls({ banner, tvs, onPushed, onClose, defaultDate }) {
  const today = new Date().toISOString().slice(0, 10);
  const [mode, setMode] = useState("interstitial"); // "interstitial" | "fullscreen"
  const [every, setEvery] = useState(3);
  const [allTvs, setAllTvs] = useState(true);
  const [selected, setSelected] = useState({});
  const [pushing, setPushing] = useState(false);
  const [when, setWhen] = useState("now");           // "now" | "schedule"
  const [startDate, setStartDate] = useState(defaultDate || today);
  const [endDate, setEndDate] = useState(defaultDate || today);

  const push = async () => {
    const tv_ids = allTvs ? null : Object.keys(selected).filter((k) => selected[k]);
    if (!allTvs && (!tv_ids || tv_ids.length === 0)) return toast.error("Pick at least one TV");
    setPushing(true);
    try {
      if (when === "schedule") {
        if (endDate < startDate) { setPushing(false); return toast.error("End date must be on/after start date"); }
        await api.post("/themes/schedule", {
          media_id: banner.id, tv_ids, mode, every: Number(every),
          start_date: startDate, end_date: endDate,
        });
        toast.success(`📅 Scheduled — auto-shows ${startDate}, clears after ${endDate}`);
      } else {
        const { data } = await api.post("/themes/push", {
          media_id: banner.id, tv_ids, mode, every: Number(every),
        });
        toast.success(`🎉 Theme pushed to ${data.count} TV${data.count !== 1 ? "s" : ""}`);
      }
      onPushed && onPushed();
      onClose();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Failed");
    }
    setPushing(false);
  };

  return (
    <>
      <div className="space-y-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">How to display it</p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setMode("interstitial")} data-testid="theme-mode-interstitial"
              className={`rounded-lg border p-3 text-left transition-colors ${mode === "interstitial" ? "border-primary bg-primary/10" : "border-border bg-secondary/40 hover:border-muted-foreground"}`}>
              <p className="text-sm font-semibold flex items-center gap-1.5"><Repeat className="w-4 h-4" /> Between slides</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Offers keep running; greeting pops in every few slides</p>
            </button>
            <button type="button" onClick={() => setMode("fullscreen")} data-testid="theme-mode-fullscreen"
              className={`rounded-lg border p-3 text-left transition-colors ${mode === "fullscreen" ? "border-primary bg-primary/10" : "border-border bg-secondary/40 hover:border-muted-foreground"}`}>
              <p className="text-sm font-semibold flex items-center gap-1.5"><Maximize className="w-4 h-4" /> Full screen</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Greeting takes over the whole screen (nothing else)</p>
            </button>
          </div>
        </div>

        {mode === "interstitial" && (
          <>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm">Show greeting every</p>
              <Select value={String(every)} onValueChange={(v) => setEvery(Number(v))}>
                <SelectTrigger className="w-40" data-testid="theme-every-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2, 3, 4, 5, 6, 8].map((n) => <SelectItem key={n} value={String(n)}>every {n} slides</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-[11px] text-amber-400/90">
              Note: this weaves into each TV's current playlist — the TV needs a base playlist/offer slides assigned.
            </p>
          </>
        )}

        {/* When: now vs schedule */}
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">When</p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setWhen("now")} data-testid="theme-when-now"
              className={`rounded-lg border p-3 text-left transition-colors ${when === "now" ? "border-primary bg-primary/10" : "border-border bg-secondary/40 hover:border-muted-foreground"}`}>
              <p className="text-sm font-semibold flex items-center gap-1.5"><Send className="w-4 h-4" /> Push now</p>
            </button>
            <button type="button" onClick={() => setWhen("schedule")} data-testid="theme-when-schedule"
              className={`rounded-lg border p-3 text-left transition-colors ${when === "schedule" ? "border-primary bg-primary/10" : "border-border bg-secondary/40 hover:border-muted-foreground"}`}>
              <p className="text-sm font-semibold flex items-center gap-1.5"><CalendarClock className="w-4 h-4" /> Schedule</p>
            </button>
          </div>
          {when === "schedule" && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Auto-show on</Label>
                <Input type="date" value={startDate} min={today} onChange={(e) => setStartDate(e.target.value)} className="mt-1" data-testid="theme-start-date" />
              </div>
              <div>
                <Label className="text-xs">Auto-hide after</Label>
                <Input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1" data-testid="theme-end-date" />
              </div>
              <p className="text-[11px] text-muted-foreground col-span-2">
                Turns on at the start of “{startDate}” and clears itself the day after “{endDate}”.
              </p>
            </div>
          )}
        </div>

        {/* TV scope */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-sm">Push to all TVs</p>
            <p className="text-xs text-muted-foreground">{allTvs ? "Every paired screen" : "Choose specific screens below"}</p>
          </div>
          <Switch checked={allTvs} onCheckedChange={setAllTvs} data-testid="theme-all-tvs-toggle" />
        </div>
        {!allTvs && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
            {tvs.map((tv) => (
              <button key={tv.id} type="button" onClick={() => setSelected((s) => ({ ...s, [tv.id]: !s[tv.id] }))}
                data-testid={`theme-tv-${tv.id}`}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 border text-left transition-colors ${selected[tv.id] ? "border-primary bg-primary/10" : "border-border bg-secondary/40 hover:border-muted-foreground"}`}>
                <Tv className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm truncate flex-1">{tv.name}</span>
                {selected[tv.id] && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
              </button>
            ))}
            {tvs.length === 0 && <p className="text-sm text-muted-foreground col-span-full py-2">No TVs paired yet.</p>}
          </div>
        )}
      </div>

      <DialogFooter className="mt-4">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={push} disabled={pushing} data-testid="theme-push-btn">
          {pushing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
            : when === "schedule" ? <><CalendarClock className="w-4 h-4 mr-2" /> Schedule it</>
            : <><Send className="w-4 h-4 mr-2" /> Approve & Push</>}
        </Button>
      </DialogFooter>
    </>
  );
}

function GenerateDialog({ event, tvs, onClose, onPushed }) {
  const [headline, setHeadline] = useState(event.headline || "");
  const [style, setStyle] = useState("elegant");
  const [generating, setGenerating] = useState(false);
  const [banner, setBanner] = useState(null);

  const generate = async () => {
    setGenerating(true);
    setBanner(null);
    try {
      const { data } = await api.post("/themes/generate", {
        event: event.name, scene: event.scene, palette: event.palette,
        headline: headline.trim(), style,
      });
      setBanner(data);
      toast.success("Banner ready — review and push when happy");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Generation failed");
    }
    setGenerating(false);
  };

  return (
    <DialogContent className="max-w-3xl w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-lg md:text-xl flex items-center gap-2 pr-6">
          <span>{event.emoji}</span> {event.name} theme
        </DialogTitle>
        <DialogDescription className="sr-only">Generate and push a festive greeting banner for {event.name}.</DialogDescription>
      </DialogHeader>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div>
            <Label>Greeting text</Label>
            <Input value={headline} onChange={(e) => setHeadline(e.target.value)}
              placeholder="Happy 4th of July" className="mt-1" data-testid="theme-headline-input" />
          </div>
          <div>
            <Label>Design style</Label>
            <Select value={style} onValueChange={setStyle}>
              <SelectTrigger className="mt-1" data-testid="theme-style-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="elegant">✨ Elegant &amp; Luxury (gold, cinematic)</SelectItem>
                <SelectItem value="bold">🔥 Bold &amp; Modern (high-impact)</SelectItem>
                <SelectItem value="festive">🎊 Warm &amp; Festive (glow, lights)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] text-muted-foreground/80">
            AI paints a premium full-screen greeting banner in the {event.name} palette (no offer text —
            run promos through your slides). Generation takes ~20–40s.
          </p>
          <Button onClick={generate} disabled={generating} className="w-full" data-testid="theme-generate-btn">
            {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Painting your banner…</>
              : <><Wand2 className="w-4 h-4 mr-2" /> {banner ? "Regenerate" : "Generate banner"}</>}
          </Button>
        </div>

        <div>
          <Label>Preview (1920×1080)</Label>
          <div className="mt-1 aspect-video rounded-lg overflow-hidden border border-border bg-black flex items-center justify-center" data-testid="theme-preview">
            {generating ? (
              <div className="text-center text-muted-foreground text-sm px-4">
                <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-primary" />
                Generating a premium festive banner…
              </div>
            ) : banner ? (
              <img src={mediaUrl(banner.id)} alt="banner" className="w-full h-full object-contain" />
            ) : (
              <div className="text-center text-muted-foreground/60 text-sm px-4">
                <PartyPopper className="w-8 h-8 mx-auto mb-2 opacity-40" />
                Your banner appears here
              </div>
            )}
          </div>
        </div>
      </div>

      {banner && (
        <div className="mt-2 border-t border-border pt-4">
          <PushControls banner={banner} tvs={tvs} onPushed={onPushed} onClose={onClose} defaultDate={event.date} />
        </div>
      )}
    </DialogContent>
  );
}

function RepushDialog({ banner, tvs, onClose, onPushed }) {
  return (
    <DialogContent className="max-w-lg w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-lg md:text-xl pr-6">Run this theme</DialogTitle>
        <DialogDescription className="sr-only">Choose how and when to display this theme banner on your TVs.</DialogDescription>
      </DialogHeader>
      <div className="aspect-video rounded-lg overflow-hidden border border-border bg-black mb-2">
        <img src={mediaUrl(banner.id)} alt="banner" className="w-full h-full object-contain" />
      </div>
      <PushControls banner={banner} tvs={tvs} onPushed={onPushed} onClose={onClose} />
    </DialogContent>
  );
}

export default function Themes() {
  const [suggestions, setSuggestions] = useState([]);
  const [tvs, setTvs] = useState([]);
  const [recent, setRecent] = useState([]);
  const [status, setStatus] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [active, setActive] = useState(null);   // event object for GenerateDialog
  const [repush, setRepush] = useState(null);    // banner media doc for RepushDialog
  const [query, setQuery] = useState("");
  const [eventQuery, setEventQuery] = useState("");
  const [clearing, setClearing] = useState(false);

  const loadRecent = useCallback(() =>
    api.get("/media").then((r) => setRecent((r.data || []).filter((m) => m.is_theme))), []);
  const loadStatus = useCallback(() =>
    api.get("/themes/status").then((r) => setStatus(r.data)).catch(() => {}), []);
  const loadSchedules = useCallback(() =>
    api.get("/themes/schedules").then((r) => setSchedules(r.data)).catch(() => {}), []);

  useEffect(() => {
    api.get("/themes/suggestions").then((r) => setSuggestions(r.data)).catch(() => {});
    api.get("/tvs").then((r) => setTvs(r.data)).catch(() => {});
    loadRecent();
    loadStatus();
    loadSchedules();
  }, [loadRecent, loadStatus, loadSchedules]);

  const afterChange = () => { loadRecent(); loadStatus(); loadSchedules(); };
  const daysLabel = (d) => (d === 0 ? "Today" : d === 1 ? "Tomorrow" : `in ${d} days`);

  const cancelSchedule = async (id) => {
    try {
      await api.delete(`/themes/schedules/${id}`);
      toast.success("Schedule cancelled");
      afterChange();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not cancel");
    }
  };

  const clearTheme = async (tvId) => {
    setClearing(true);
    try {
      const { data } = await api.post("/themes/clear", { tv_ids: tvId ? [tvId] : null });
      if (data.count > 0) toast.success(`Theme removed from ${data.count} TV${data.count !== 1 ? "s" : ""}`);
      else toast.info("No TVs currently showing a theme");
      loadStatus();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not clear");
    }
    setClearing(false);
  };

  const liveTvs = status.filter((s) => s.theme);
  const filtered = recent.filter((m) =>
    (m.theme_event || m.original_filename || "").toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-5xl">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-display font-bold tracking-tight flex items-center gap-2">
            <PartyPopper className="w-6 h-6 text-primary" /> Seasonal Themes
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            AI-designed festive greetings — you choose the screens and approve before anything goes live.
          </p>
        </div>
        <Button variant="secondary" size="sm" className="flex-shrink-0" onClick={() => clearTheme()} disabled={clearing} data-testid="theme-clear-all-btn">
          {clearing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Removing…</>
            : <><EyeOff className="w-4 h-4 mr-1.5" /> Hide theme from all TVs</>}
        </Button>
      </div>

      {/* Currently live — which theme is on which TV */}
      <div className="rounded-xl border border-border bg-card p-4 md:p-5 mb-8" data-testid="theme-status">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
          <Radio className={`w-3.5 h-3.5 ${liveTvs.length ? "text-green-500 live-dot" : ""}`} /> Now showing on your TVs
        </p>
        {status.length === 0 ? (
          <p className="text-sm text-muted-foreground">No TVs paired yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {status.map((s) => (
              <div key={s.tv_id} className="flex items-center justify-between gap-3 bg-secondary/50 rounded-lg px-3 py-2.5" data-testid={`theme-status-${s.tv_id}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <Tv className={`w-4 h-4 flex-shrink-0 ${s.theme ? "text-primary" : "text-muted-foreground"}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{s.name}</p>
                    {s.theme ? (
                      <p className="text-[11px] text-primary flex items-center gap-1">
                        {s.theme.event} · {s.theme.mode === "fullscreen" ? "full screen" : `every ${s.theme.every} slides`}
                      </p>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">No theme</p>
                    )}
                  </div>
                </div>
                {s.theme && (
                  <Button size="sm" variant="ghost" className="h-7 text-xs px-2 flex-shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => clearTheme(s.tv_id)} data-testid={`theme-status-clear-${s.tv_id}`}>
                    <EyeOff className="w-3.5 h-3.5 mr-1" /> Stop
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scheduled themes */}
      {schedules.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4 md:p-5 mb-8" data-testid="theme-schedules">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5" /> Scheduled themes
          </p>
          <div className="space-y-2">
            {schedules.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 bg-secondary/50 rounded-lg px-3 py-2.5" data-testid={`theme-schedule-${s.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{s.label}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {s.applied ? "🟢 Live now · " : "⏳ "}Shows {s.start_date} → clears after {s.end_date}
                    {" · "}{s.mode === "fullscreen" ? "full screen" : `every ${s.every} slides`}
                    {s.tv_ids ? ` · ${s.tv_ids.length} TV(s)` : " · all TVs"}
                  </p>
                </div>
                <Button size="sm" variant="ghost" className="h-7 text-xs px-2 flex-shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => cancelSchedule(s.id)} data-testid={`theme-schedule-cancel-${s.id}`}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancel
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming events */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Create a new greeting</p>
        <div className="relative w-full max-w-xs">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={eventQuery} onChange={(e) => setEventQuery(e.target.value)} placeholder="Search holiday… (e.g. Thanksgiving)"
            className="pl-8 h-9" data-testid="theme-event-search" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        {suggestions.filter((s) => s.name.toLowerCase().includes(eventQuery.toLowerCase())).map((s, i) => (
          <button key={s.id} onClick={() => setActive(s)} data-testid={`theme-suggestion-${s.id}`}
            className={`text-left rounded-xl border p-4 transition-all hover:-translate-y-0.5 ${i === 0 ? "border-primary/60 bg-primary/5 ring-1 ring-primary/30" : "border-border bg-card hover:border-muted-foreground"}`}>
            <div className="flex items-center justify-between">
              <span className="text-3xl">{s.emoji}</span>
              <span className="text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1 text-muted-foreground">
                <CalendarDays className="w-3 h-3" /> {daysLabel(s.days_away)}
              </span>
            </div>
            <h3 className="font-display font-bold text-lg mt-2">{s.name}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{s.headline}</p>
            <span className="inline-flex items-center gap-1 text-xs text-primary font-medium mt-3">
              <Wand2 className="w-3.5 h-3.5" /> Create banner
            </span>
          </button>
        ))}
        {suggestions.length === 0 && (
          <p className="text-sm text-muted-foreground col-span-full">No upcoming events in the calendar window.</p>
        )}
      </div>

      {/* Your theme banners — searchable */}
      {recent.length > 0 && (
        <div>
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Your theme banners ({recent.length})</p>
            <div className="relative w-full max-w-xs">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search themes…"
                className="pl-8 h-9" data-testid="theme-search-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map((m) => (
              <div key={m.id} className="rounded-lg overflow-hidden border border-border bg-card" data-testid={`theme-recent-${m.id}`}>
                <div className="aspect-video bg-black">
                  <img src={mediaUrl(m.id)} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-2 flex items-center justify-between gap-2">
                  <span className="text-xs truncate">{m.theme_event || "Theme"}</span>
                  <Button size="sm" variant="secondary" className="h-7 text-xs px-2 flex-shrink-0" onClick={() => setRepush(m)} data-testid={`theme-run-${m.id}`}>
                    <Send className="w-3 h-3 mr-1" /> Run
                  </Button>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="text-sm text-muted-foreground col-span-full py-2">No themes match “{query}”.</p>
            )}
          </div>
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
        {active && <GenerateDialog event={active} tvs={tvs} onClose={() => setActive(null)} onPushed={afterChange} />}
      </Dialog>
      <Dialog open={!!repush} onOpenChange={(o) => { if (!o) setRepush(null); }}>
        {repush && <RepushDialog banner={repush} tvs={tvs} onClose={() => setRepush(null)} onPushed={afterChange} />}
      </Dialog>
    </div>
  );
}
